import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
import { ClipboardCheck, Search, Trash2 } from "lucide-react";
import images from "../../assets/images";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { DateSelector } from "../../components/DateSelector/DateSelector";
import ConfirmModal from "../../components/modal/ConfirmModal";

import {
  useDepartmentStore,
  useFireSafetyAudits,
  usePremisesStore,
  useRegionStore,
  usePerformAudits,
  useOrgUsersStore,
  useRoleManageStore,
} from "../../store/store";
import CreateAudits from "./CreateAudits";
import AuditCheckList from "./AuditCheckList";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Audit, AuditFormData } from "../../types/api";
import { usePermissions } from "../../hooks/usePermission";
import { SAFETYAUDIT_MODULE } from "../../constants/modules";
import { useAuth } from "../../auth/AuthProvider";
import { PremisesData } from "../Budgeting/types/budgeting";

const Audits: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPremises, setSelectedPremises] = useState("");
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null, });
  const [premisesData, setPremisesData] = useState<PremisesData[]>([]);
  const [moduleName, setModuleName] = useState<string>("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [isCheckListModalOpen, setCheckListModalOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isView, setIsView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [auditorUsers, setAuditorUsers] = useState<any[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [auditToDelete, setAuditToDelete] = useState<string | null>(null);
  const location = useLocation();
  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/fires-safety-audits")
    ) {
      return "fire_system";
    }
    return undefined;
  };
  const myModuleName = getModuleName() || "";
  // store
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const { rolesData, fetchAllRoles } = useRoleManageStore();
  const { departments, fetchAllDepartments } = useDepartmentStore();

  const { fetchAudits, audits, fetchAuditsById, currentAudit } =
    usePerformAudits();
  const { user, roleData } = useAuth();

  const { allAudits, getAllAudits, getAllChecklist, createAudit, deleteAudit } =
    useFireSafetyAudits();

  const { canCreate, canRead, canUpdate, canDelete } = usePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchPremises(),
        fetchRegions(),
        fetchAllDepartments(),
        fetchOrgUsers(),
        fetchAllRoles(),
      ]);
      setModuleName(myModuleName);
    };
    fetchData();
  }, [
    fetchPremises,
    fetchRegions,
    fetchAllDepartments,
    fetchOrgUsers,
    fetchAllRoles,
    myModuleName,
  ]);

  useEffect(() => {
    if (moduleName) {
      if (roleData?.roleName === "auditor") {
        fetchAudits(user?.id || "", moduleName);
      } else {
        getAllAudits(moduleName, selectedRegion, selectedPremises);
      }
    }
  }, [moduleName, roleData?.roleName, user?.id, getAllAudits, fetchAudits, selectedRegion, selectedPremises,]);

  // Load premises data for filtering
  useEffect(() => {
    if (allPremises.length > 0) {
      const mappedPremisesData = allPremises.map((premise: any) => ({
        id: premise._id || premise.id,
        premisesName: premise.premisesName,
        addressLine1: premise.addressLine1,
        townCity: premise.townCity,
        region: premise.region,
        postcode: premise.postcode,
      }));
      setPremisesData(mappedPremisesData);
    }
  }, [allPremises]);

  useEffect(() => {
    if (rolesData && orgUsersData) {
      const auditorRole = rolesData.find(
        (role: any) => role.roleName === "auditor",
      );

      if (auditorRole) {
        const filteredAuditorUsers = orgUsersData.filter(
          (user: any) => user.role === auditorRole._id,
        );
        setAuditorUsers(filteredAuditorUsers);
      }
    }
  }, [rolesData, orgUsersData]);

  const originalAuditData = useMemo(() => {
    return roleData?.roleName === "auditor" ? audits : allAudits;
  }, [audits, allAudits, roleData?.roleName]);

  const processedAudits = useMemo(() => {
    const auditData = originalAuditData;

    if (!auditData.length || !allPremises.length || !departments.length) {
      return [];
    }

    const premiseMap = new Map();
    allPremises.forEach((p) => {
      // Handle both _id and id fields for premise identification
      const premiseName =
        p.premisesName?.trim() || p.premisesName || "Unknown Premise";
      if (p._id) {
        premiseMap.set(p._id, premiseName);
      }
      if (p.id) {
        premiseMap.set(p.id, premiseName);
      }
    });
    const departmentMap = new Map(
      departments.map((d) => [d._id, d.departmentName.trim()]),
    );

    let processedData = auditData.map((audit, index) => {
      let departmentDisplay;
      if (Array.isArray(audit.department)) {
        let departmentNames = audit.department
          .map((deptId) => departmentMap.get(deptId))
          .filter(Boolean);
        departmentDisplay = departmentNames;
      } else {
        departmentDisplay =
          departmentMap.get(audit.department) ?? audit.department;
      }
      let assignedAuditorsDisplay;
      if (Array.isArray(audit.assignedAuditors)) {
        // Filter auditorUsers to find matching auditors and get their names
        const auditorNames = audit.assignedAuditors
          .map((auditorId) => {
            const auditor = auditorUsers.find((user) => user._id === auditorId);
            return auditor
              ? `${auditor.firstName || ""} ${auditor.lastName || ""}`.trim()
              : auditorId;
          })
          .filter(Boolean);
        assignedAuditorsDisplay = auditorNames.join(", ");
      } else {
        const auditor = auditorUsers.find(
          (user) => user._id === audit.assignedAuditors,
        );
        assignedAuditorsDisplay = auditor
          ? `${auditor.firstName || ""} ${auditor.lastName || ""}`.trim()
          : audit.assignedAuditors;
      }

      const premiseName = premiseMap.get(audit.premise);
      if (!premiseName && audit.premise) {
        const foundPremise = allPremises.find(
          (p) => p._id === audit.premise || p.id === audit.premise,
        );
        if (foundPremise) {
        }
      }

      return {
        ...audit,
        premise: premiseName ?? (audit.premise || "Unknown Premise"),
        department: departmentDisplay,
        assignedAuditors: assignedAuditorsDisplay,
        originalIndex: index,
      };
    });

    // Apply region filter
    if (selectedRegion) {
      processedData = processedData.filter((audit) => {
        const premises = premisesData.find(
          (p) => p.premisesName === audit.premise,
        );
        return premises?.region === selectedRegion;
      });
    }
    // Apply premises filter
    if (selectedPremises) {
      processedData = processedData.filter((audit) => {
        const regionsData = premisesData.find((p) => p.id === selectedPremises);
        return audit.premise === regionsData?.premisesName;
      });
    }

    // Apply Date filter
    if (dateRange.start && dateRange.end) {
      const startDate = dateRange.start;
      const endDate = dateRange.end;

      processedData = processedData.filter((audit) => {
        if (!audit.startDate) return false;

        const rowDate = new Date(audit.startDate);

        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        return rowDate >= start && rowDate <= end;
      });
    }
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      processedData = processedData.filter((audit) => {
        if (audit._id?.toLowerCase().includes(searchLower)) return true;
        if (audit.premise?.toLowerCase().includes(searchLower)) return true;
        if (Array.isArray(audit.department)) {
          if (
            audit.department.some((dept) =>
              dept?.toLowerCase().includes(searchLower),
            )
          )
            return true;
        } else if (audit.department?.toLowerCase().includes(searchLower)) {
          return true;
        }

        if (audit.assignedAuditors?.toLowerCase().includes(searchLower)) {
          return true;
        }

        if (audit.audit_status?.toLowerCase().includes(searchLower))
          return true;

        if (audit.startDate?.toLowerCase().includes(searchLower)) return true;

        return false;
      });
    }

    return processedData;
  }, [
    originalAuditData,
    allPremises,
    departments,
    auditorUsers,
    selectedRegion,
    selectedPremises,
    dateRange,
    premisesData,
    searchTerm,
  ]);

  const statusCounts = useMemo(() => {
    let total = 0;
    let scheduledCount = 0;
    let inProgressCount = 0;
    let completedCount = 0;

    processedAudits.forEach((audit) => {
      total++;
      const status = audit.audit_status;
      if (status === "Scheduled") {
        scheduledCount++;
      } else if (status === "In Progress") {
        inProgressCount++;
      } else if (status === "Completed") {
        completedCount++;
      }
    });

    return {
      total,
      scheduledCount,
      inProgressCount,
      completedCount,
    };
  }, [processedAudits]);

  const statusCellRenderer = useCallback((params: ICellRendererParams) => {
    const status = params.data.audit_status;
    const color =
      status === "Scheduled"
        ? "text-red-500"
        : status === "In Progress"
          ? "text-orange-500"
          : status === "Completed"
            ? "text-green-500"
            : "text-gray-500";

    return <span className={`font-medium ${color}`}>{status}</span>;
  }, []);

  const auditActionCellRenderer = useCallback((params: ICellRendererParams) => {
    return (
      <div className="">
        {params.data.audit_status === "Scheduled" ||
          params.data.audit_status === "In Progress" ? (
          <Link
            to={`/perform-audits/check-list`}
            onClick={() => {
              fetchAuditsById(params.data._id);
            }}
            state={
              params.data.audit_status === "In Progress"
                ? { audit: params.data, currentAudit, isView: false }
                : { audit: params.data }
            }
          >
            <button className="focus:outline-none flex justify-center items-center text-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`lucide lucide-clipboard-clock-icon lucide-clipboard-clock ${params.data.audit_status === "Scheduled"
                  ? "text-red-500"
                  : "text-orange-500"
                  }`}
              >
                <path d="M16 14v2.2l1.6 1" />
                <path d="M16 4h2a2 2 0 0 1 2 2v.832" />
                <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2" />
                <circle cx="16" cy="16" r="6" />
                <rect x="8" y="2" width="8" height="4" rx="1" />
              </svg>
              <p
                className={`text-sm ${params.data.audit_status === "Scheduled"
                  ? "text-red-500"
                  : "text-orange-500"
                  } pt-2 font-semibold`}
              >
                {params.data.audit_status}
              </p>
            </button>
          </Link>
        ) : (
          <button className="focus:outline-none flex justify-center items-center gap-2">
            <ClipboardCheck className="w-6 h-6 cursor-pointer text-green-500" />
            <p className="text-sm text-green-500 pt-2 font-semibold">
              Completed
            </p>
          </button>
        )}
      </div>
    );
  }, []);

  const performAuditsActionCellRenderer = useCallback(
    (params: ICellRendererParams) => {
      return (
        <div className="flex gap-3 items-center">
          <Link
            to={`/perform-audits/check-list`}
            state={{ audit: params.data, isView: true }}
            onClick={() => {
              fetchAuditsById(params.data._id);
            }}
          >
            <img
              src={images.viewIcon}
              alt="View"
              className="w-6 h-6 cursor-pointer"
            />
          </Link>

          {/* Edit icon - only show if status is not "Completed" */}
          {params.data.audit_status !== "Completed" && (
            <Link
              to={`/perform-audits/check-list`}
              state={{ audit: params.data, isView: false }}
              onClick={() => {
                fetchAuditsById(params.data._id);
              }}
            >
              <img
                src={images.editIcon}
                alt="Edit"
                className="w-6 h-6 cursor-pointer"
              />
            </Link>
          )}
        </div>
      );
    },
    [fetchAuditsById],
  );

  const actionCellRenderer = useCallback(
    (params: ICellRendererParams) => {
      // Show PerformAudits actions if role is "auditor"
      if (roleData?.roleName === "auditor") {
        return performAuditsActionCellRenderer(params);
      }

      // Default Audits actions
      return (
        <div className="flex gap-2 items-center">
          {/* View modal */}
          {canRead(SAFETYAUDIT_MODULE) && (
            <button
              onClick={() => handleView(params.data)}
              title="View"
              className="transition-transform"
            >
              <img src={images.viewIcon} className="iconTd" alt="View" />
            </button>
          )}

          {/* Edit */}
          {canUpdate(SAFETYAUDIT_MODULE) && (
            <button
              onClick={() => handleEdit(params.data)}
              title="Edit"
              className="transition-transform"
            >
              <img src={images.editIcon} className="iconTd" alt="Edit" />
            </button>
          )}

          {/* Delete */}
          {canDelete(SAFETYAUDIT_MODULE) && (
            <button
              onClick={() => {
                setAuditToDelete(params.data._id);
                setIsDeleteModalOpen(true);
              }}
              title="Delete"
              className="transition-transform"
            >
              <Trash2 size={22} className="text-red-500" />
            </button>
          )}
        </div>
      );
    },
    [
      canRead,
      canUpdate,
      canDelete,
      deleteAudit,
      roleData?.roleName,
      performAuditsActionCellRenderer,
    ],
  );

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: "Audit ID",
        field: "_id",
        flex: 1,
        minWidth: 100,
        cellStyle: {
          paddingTop: "15px",
        },
      },
      {
        headerName: "Audit Date",
        field: "startDate",
        flex: 1,
        minWidth: 120,
        cellStyle: {
          paddingTop: "15px",
        },
      },
      {
        headerName: "Premise Name / ID",
        field: "premise",
        flex: 1.5,
        cellStyle: {
          paddingTop: "15px",
        },
        minWidth: 150,
      },
      {
        headerName: "Area / Department Audited",
        field: "department",
        flex: 1.8,
        minWidth: 180,
        cellStyle: {
          paddingTop: "15px",
        },
      },
      {
        headerName: "Auditor Name(s)",
        field: "assignedAuditors",
        flex: 2,
        minWidth: 150,
        cellStyle: {
          paddingTop: "15px",
        },
      },
    ];

    if (roleData?.roleName === "auditor") {
      baseColumns.push({
        headerName: "Audit Action",
        field: "auditAction",
        flex: 1.2,
        cellStyle: {
          paddingTop: "10px",
        },
        cellRenderer: auditActionCellRenderer,
      });
    } else {
      baseColumns.push({
        headerName: "Audit Status",
        field: "audit_status",
        flex: 0,
        minWidth: 100,
        cellStyle: {
          paddingLeft: "20px",
          paddingTop: "15px",
        },
        cellRenderer: statusCellRenderer,
      });
    }

    // Add Action column
    baseColumns.push({
      headerName: "Action",
      field: "actions",
      flex: 1,
      minWidth: 120,
      cellStyle: {
        paddingTop: "15px",
      },
      cellRenderer: actionCellRenderer,
      suppressSizeToFit: false,
    });

    return baseColumns;
  }, [
    statusCellRenderer,
    actionCellRenderer,
    auditActionCellRenderer,
    roleData?.roleName,
  ]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  const handleView = (processedAudit: any) => {
    const originalAudit = originalAuditData[processedAudit.originalIndex];
    setSelectedAudit(originalAudit);
    setIsView(true);
    setIsEdit(false);
    setModalOpen(true);
  };

  const handleEdit = (processedAudit: any) => {
    const originalAudit = originalAuditData[processedAudit.originalIndex];
    setSelectedAudit(originalAudit);
    setIsEdit(true);
    setIsView(false);
    setModalOpen(true);
  };
  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    setSelectedPremises("");
  };

  const handlePremisesChange = (premises: string) => {
    setSelectedPremises(premises);
  };

  const handleCreate = () => {
    setSelectedAudit(null);
    setIsEdit(false);
    setIsView(false);
    setModalOpen(true);
  };

  const handleCheckList = () => {
    if (moduleName === "hazardous_materials") {
      navigate("/hazardous-materials/audits/check-list");
    } else {
      navigate("/fire-safety-audits/check-list");
    }
    getAllChecklist(moduleName);
  };

  const handleCheckListSubmit = (listId: string) => {
    setCheckListModalOpen(false);
    setSelectedAudit(null);
  };

  return (
    <>
      <style>
        {`
          .audits-container {
            width: 100%;
            max-width: 100%;
            padding: 0 25px;
            margin: 0 auto;
          }
          .tableSearchBlock, .cardNum, .p-4 {
            width: 100%;
            padding: 0;
            margin: 0;
          }
          .ag-theme-alpine {
            width: 100%;
            margin: 0;
          }
        `}
      </style>
      <div className="flex w-full mt-20">
        <div className="audits-container px-2">
          <div className="tableSearchBlock d-flex justify-content-between">
            <div className="leftTableSearchBlock">
              <h2>
                {roleData?.roleName === "auditor"
                  ? "Perform Audit"
                  : moduleName === "hazardous_materials"
                    ? "Hazardous Materials Audits"
                    : "Fire Safety Audits"}
              </h2>
            </div>
            <div className="flex gap-4">
              <Dropdown
                type="region"
                selectedValue={selectedRegion}
                onChange={handleRegionChange}
              />
              <Dropdown
                type="premises"
                selectedValue={selectedPremises}
                onChange={handlePremisesChange}
                selectedRegion={selectedRegion}
              />
              <DateSelector
                onDateChange={(start, end) => setDateRange({ start, end })}
              />
            </div>
          </div>

          <div className="cardNum mt-4 mb-4">
            <div className="row">
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">Total Audits</span>
                    <h3>{statusCounts.total}</h3>
                  </div>
                  <div className="cardIcon cardIconColor1">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">Scheduled Audits</span>
                    <h3>{statusCounts.scheduledCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor2">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">In Progress Audits</span>
                    <h3>{statusCounts.inProgressCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor3">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="card">
                  <div className="cardIconCont">
                    <span className="font-medium text-md">Completed Audits</span>
                    <h3>{statusCounts.completedCount}</h3>
                  </div>
                  <div className="cardIcon cardIconColor4">
                    <img className="iconTd" src={images.premisesIcon} alt="" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-[#ffffff] rounded-md mt-4">
            <div className="relative flex justify-between items-center">
              <div className="flex w-10/12 justify-start items-center">
                <Search className="absolute left-3 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search audits, premises, departments..."
                  className="border-2 border-[#a5b7cf] mr-5 w-[] px-10 py-2 rounded"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
              <div className="dashHeaderRight w-full">
                {/* Buttons - Only show for regular audits, not auditor */}
                {roleData?.roleName !== "auditor" && (
                  <div className="flex gap-3">
                    {canRead(SAFETYAUDIT_MODULE) && (
                      <button
                        onClick={handleCheckList}
                        className="flex items-center gap-2 font-semibold bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                      >
                        <img src={images.tasksIcon} width={24} alt="" />
                        CHECK LIST
                      </button>
                    )}
                    {canCreate(SAFETYAUDIT_MODULE) && (
                      <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 font-semibold bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                      >
                        <img src={images.addPremiessIcon} width={24} alt="" />
                        CREATE AUDIT
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="ag-theme-alpine mt-4">
                <AgGridReact
                  rowData={processedAudits}
                  columnDefs={columnDefs}
                  rowHeight={50}
                  headerHeight={65}
                  paginationPageSize={5}
                  pagination={true}
                  domLayout="autoHeight"
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  getRowStyle={() => ({
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginBottom: "8px",
                    borderRadius: "4px",
                    backgroundColor: "white",
                  })}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Only show modals for regular audits, not auditor */}
      {roleData?.roleName !== "auditor" && (
        <>
          <CreateAudits
            isOpen={isModalOpen}
            isEdit={isEdit}
            isView={isView}
            onClose={() => {
              setModalOpen(false);
              setIsEdit(false);
              setSelectedAudit(null);
            }}
            onSubmit={(data: AuditFormData) => createAudit(data, moduleName)}
            moduleName={moduleName || ""}
            initialData={
              selectedAudit && (isEdit || isView)
                ? {
                  _id: (selectedAudit as any)._id,
                  premise: (selectedAudit as any).premise,
                  auditFrequency: (selectedAudit as any).auditFrequency || "",
                  startDate:
                    (selectedAudit as any).startDate ||
                    (selectedAudit as any).auditDate ||
                    "",
                  reminder: (selectedAudit as any).reminder || "",
                  checklist: (selectedAudit as any).checklist || [],
                  moduleName: moduleName || "",
                  assignedAuditors:
                    (selectedAudit as any).assignedAuditors ||
                    (selectedAudit as any).auditorName ||
                    "",
                  auditType: Array.isArray((selectedAudit as any).auditType)
                    ? (selectedAudit as any).auditType[0] || ""
                    : (selectedAudit as any).auditType || "",
                  department: Array.isArray((selectedAudit as any).department)
                    ? (selectedAudit as any).department
                    : (selectedAudit as any).department
                      ? [(selectedAudit as any).department]
                      : [],
                  schedule: Array.isArray((selectedAudit as any).schedule)
                    ? (selectedAudit as any).schedule[0] || ""
                    : (selectedAudit as any).schedule || "",
                  audit_status:
                    selectedAudit.audit_status === "Scheduled"
                      ? "Scheduled"
                      : "Completed",
                }
                : undefined
            }
          />

          <AuditCheckList
            isOpen={isCheckListModalOpen}
            onClose={() => {
              setCheckListModalOpen(false);
              setSelectedAudit(null);
            }}
            onSubmit={handleCheckListSubmit}
          />
        </>
      )}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Are you sure you want to delete this audit?"
        confirmLabel="YES"
        cancelLabel="NO"
        onConfirm={async () => {
          if (auditToDelete) {
            try {
              await deleteAudit(auditToDelete, moduleName);
            } catch (error) {
              console.error("Error deleting audit:", error);
            } finally {
              setIsDeleteModalOpen(false);
              setAuditToDelete(null);
            }
          }
        }}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setAuditToDelete(null);
        }}
      />
    </>
  );
};

export default Audits;
